/*
 * @Author: Yun 
 * @Date: 2025-09-25 16:42:42 
 * @Last Modified by: Yun
 * @Last Modified time: 2025-09-25 17:05:37
 */

/**
 * ========== 环境配置管理 ==========
 * 统一管理不同环境下的配置信息
 */

// 环境类型定义
export type Environment = 'development' | 'test' | 'production';

// 环境配置接口
export interface EnvConfig {
  environment: Environment;
  baseUrl: string;
  basePath: string;
  fullBaseUrl: string;
  isDevelopment: boolean;
  isTest: boolean;
  isProduction: boolean;
}

// 获取当前环境
const getCurrentEnvironment = (): Environment => {
  // 优先使用 REACT_APP_ENV，其次使用 NODE_ENV
  const reactAppEnv = process.env.REACT_APP_ENV as Environment;
  const nodeEnv = process.env.NODE_ENV as Environment;
  
  if (reactAppEnv) {
    return reactAppEnv;
  }
  
  // 如果没有设置 REACT_APP_ENV，根据 NODE_ENV 判断
  if (nodeEnv === 'production') {
    return 'production';
  }
  
  return 'development';
};

// 获取环境配置
const getEnvConfig = (): EnvConfig => {
  const environment = getCurrentEnvironment();
  
  // 默认配置（开发环境）
  let baseUrl = 'http://localhost:3000';
  let basePath = '/';
  
  // 根据环境设置不同的配置
  if (environment === 'test') {
    baseUrl = process.env.REACT_APP_BASE_URL || 'https://newstest.cgtn.com';
    basePath = process.env.REACT_APP_BASE_PATH || '/event/2025/china2025/';
  } else if (environment === 'production') {
    baseUrl = process.env.REACT_APP_BASE_URL || 'https://news.cgtn.com';
    basePath = process.env.REACT_APP_BASE_PATH || '/event/2025/china2025/';
  }
  
  // 生成完整的基础URL
  const fullBaseUrl = process.env.REACT_APP_FULL_BASE_URL || `${baseUrl}${basePath}`;
  
  return {
    environment,
    baseUrl,
    basePath,
    fullBaseUrl,
    isDevelopment: environment === 'development',
    isTest: environment === 'test',
    isProduction: environment === 'production',
  };
};

// 导出当前环境配置
export const envConfig = getEnvConfig();

// 导出环境相关的辅助函数
export const getAssetUrl = (assetPath: string): string => {
  // 如果是开发环境，直接返回相对路径
  if (envConfig.isDevelopment) {
    return assetPath;
  }
  
  // 如果是完整URL，直接返回
  if (assetPath.startsWith('http')) {
    return assetPath;
  }
  
  // 确保路径以 / 开头
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  
  // 生产环境下，拼接完整的基础URL
  return `${envConfig.baseUrl}${envConfig.basePath}${normalizedPath.slice(1)}`;
};

// 导出微信分享图片URL生成函数
export const getWxShareImgUrl = (): string => {
  if (envConfig.isDevelopment) {
    return `${window.location.origin}/poster.jpg`;
  }
  
  return `${envConfig.fullBaseUrl}poster.jpg`;
};

// 获取JWPlayer基础路径
export const getJWPlayerBasePath = (): string => {
  if (envConfig.isDevelopment) {
    return '/jwplayer-8.32-1.0';
  }
  
  return `${envConfig.basePath}jwplayer-8.32-1.0`;
};

// 打印环境信息（仅在开发环境）
if (envConfig.isDevelopment) {
  console.log('当前环境配置:', envConfig);
}

export default envConfig;